import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useAIContext } from 'src/context/AIContextBackend';
import { useHighlightedReferences, HighlightedReference } from 'src/context/HighlightedReferencesContext';
import { useSettings } from 'src/context/SettingsContext';
import { useBrainContext } from 'src/context/BrainContext';
import { anniChatLogger } from '@annote/core/utils/debug';
import styled from 'styled-components';
import { text, colors, ui } from '@annote/core/styles';
import { CircleArrowUp, Mic, Square, X } from 'lucide-react';
import { ReactComponent as AIIcon } from 'src/components/Icon/icons/ai.svg';
import { useVoiceRecording } from 'src/hooks/useVoiceRecording';
import { transcribeAudio } from 'src/helpers/transcribeAudio';
import { setAnniChatHandler } from '@annote/core/components/proseEditor';
import { parseSuggestionTitle, ParsedTitleSegment } from 'src/utils/parseSuggestionTitle';
import { InlineNote } from 'src/components/Cards/InlineNote';
import { Z_INDEX } from 'src/constants/panelSizes';
import { AcceptRejectAll } from './AcceptRejectAll';
import { SuggestionGroups } from './SuggestionGroups';
import { ChatInput, ChatInputHandle } from './ChatInput';

const Container = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  top: 60px; /* Ensure container doesn't cover top nav bar (60px height) */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end; /* Align content to bottom */
  pointer-events: none;
  z-index: ${Z_INDEX.ANNI_CHAT};
  /* Ensure container doesn't intercept clicks on elements above it */
  touch-action: none;
`;

const Wrapper = styled.div`
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  padding: 8px 16px 16px 16px;
  pointer-events: auto;
  position: relative;
`;

const InputForm = styled.form`
  display: flex;
  align-items: center;
  gap: 0;
  width: 100%;
  min-height: 40px;
  background: ${colors.bg.primary};
  border: 1px solid ${colors.borders.input.primary};
  border-radius: 8px;
  box-shadow: ${colors.shadow.card.default};
  padding-right: 4px;

  &:focus-within {
    border-color: ${colors.borders.input.selected};
  }
`;

const SendButton = styled.button`
  flex-shrink: 0;
  background: transparent;
  border: 0;
  width: 32px;
  height: 32px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: ${colors.icon.primary};
  border-radius: 4px;

  &:hover:not(:disabled) {
    background: ${colors.bg.hover};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    display: block;
  }
`;

const MicButton = styled.button<{ isRecording: boolean }>`
  flex-shrink: 0;
  background: ${(props) => (props.isRecording ? colors.bg.hover : 'transparent')};
  border: 0;
  width: 32px;
  height: 32px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: ${(props) => (props.isRecording ? '#ef4444' : colors.icon.primary)};
  border-radius: 4px;

  &:hover:not(:disabled) {
    background: ${colors.bg.hover};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingDots = styled.span`
  color: ${colors.text.placeholder};

  @keyframes ellipsis {
    0% {
      content: '.';
    }
    33% {
      content: '..';
    }
    66% {
      content: '...';
    }
  }

  &::after {
    content: '.';
    animation: ellipsis 1.5s steps(1) infinite;
    display: inline-block;
  }
`;

const StatusMessage = styled.span`
  color: ${colors.text.description};
  font-style: italic;
  font-size: ${text.size.small};
`;

const ErrorMessage = styled.span`
  color: ${colors.text.error};
  font-size: ${text.size.small};
  display: inline-block !important;
  visibility: visible !important;
  opacity: 1 !important;
  min-width: 1px;
  white-space: nowrap;
`;

const AnniTitle = styled.span`
  font-weight: ${text.weight.medium};
  font-size: ${text.size.primary};
  color: ${colors.text.description};
  padding: 0;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const AnniBar = styled.div<{ hasAutoSuggestions: boolean }>`
  display: flex;
  flex-direction: ${(props) => (props.hasAutoSuggestions ? 'column' : 'row')};
  align-items: ${(props) => (props.hasAutoSuggestions ? 'stretch' : 'center')};
  gap: ${(props) => (props.hasAutoSuggestions ? '12px' : '8px')};
  padding: ${(props) => (props.hasAutoSuggestions ? '12px 16px' : '8px 16px')};
  border: 1px solid ${colors.borders.input.primary};
  border-radius: 8px 8px 0 0px;
  margin: 0 8px -4px 8px;
  font-family: ${text.family.primary};
  font-size: ${text.size.primary};
  color: ${colors.text.primary};
  background: ${colors.bg.primary};
  box-shadow: ${colors.shadow.card.default};
  box-sizing: border-box;
`;

const AnniBarHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SuggestionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CategoryRow = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 28px;
  padding: 4px 8px;
  margin: -4px -8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  font: inherit;

  &:hover {
    background-color: ${colors.bg.suggestion.hover};
  }
`;

const CategoryLabel = styled.span`
  font-size: ${text.size.small};
  font-weight: ${text.weight.medium};
  color: ${colors.text.description};
  white-space: nowrap;
  flex-shrink: 0;
`;

const NotesContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  overflow: hidden;
`;

const RowDismiss = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  color: ${colors.text.placeholder};
  cursor: pointer;
  border-radius: 4px;
  flex-shrink: 0;

  &:hover {
    color: ${colors.text.primary};
    background: ${colors.bg.hover};
  }
`;

/**
 * Get compact pill display for a suggestion.
 * Shows only the note references using InlineNote, or falls back to a short title.
 */
const getCompactPillDisplay = (parsedTitle: ParsedTitleSegment[]): React.ReactNode => {
  // Get only note/source references
  const refs = parsedTitle.filter((s) => s.type === 'note' || s.type === 'source');

  if (refs.length > 0) {
    // Show note names using InlineNote component with maxWidth for truncation
    return refs.map((ref, idx) => (
      <InlineNote
        key={idx}
        note={{
          id: `ref-${idx}`,
          title: ref.content,
          type: ref.type === 'source' ? 'source' : 'takeaway',
        }}
        maxWidth="150px"
      />
    ));
  }

  // Fallback: show first text segment, truncated (60 chars to allow reasonable display)
  const textSegment = parsedTitle.find((s) => s.type === 'text');
  if (textSegment) {
    const truncated = textSegment.content.length > 60 ? `${textSegment.content.slice(0, 60)}…` : textSegment.content;
    return <span title={textSegment.content}>{truncated}</span>;
  }

  return <span>Suggestion</span>;
};

const groupSuggestionsByCategory = (
  groups: Array<{ id: string; title: string; group_type: string }>,
): Record<string, Array<{ id: string; title: string; group_type: string }>> => {
  const categorized: Record<string, Array<{ id: string; title: string; group_type: string }>> = {
    'Connect together': [],
    'Continue reading': [],
    Organize: [],
    Delete: [],
  };

  groups.forEach((group) => {
    if (group.group_type === 'connect') {
      categorized['Connect together'].push(group);
    } else if (group.group_type === 'continue_reading') {
      categorized['Continue reading'].push(group);
    } else if (group.group_type === 'organize_notes') {
      categorized.Organize.push(group);
    } else if (group.group_type === 'delete' || group.group_type === 'cleanup') {
      categorized.Delete.push(group);
    }
  });

  return categorized;
};

export const AnnIChat: React.FC = () => {
  const {
    messages,
    currentStreamingMessage,
    currentStatusMessage,
    isLoading,
    error,
    isRateLimited,
    submitMessage,
    availableSuggestionGroups,
    applySuggestionGroup,
    dismissSuggestionGroupFromBar,
  } = useAIContext();
  const { setHighlightedReferences } = useHighlightedReferences();
  const { getSetting } = useSettings();
  const { customFetch } = useBrainContext();
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<ChatInputHandle>(null);
  const { isRecording, isSupported, startRecording, stopRecording } = useVoiceRecording();
  const [isTranscribing, setIsTranscribing] = useState(false);
  const highlightsRef = useRef<HighlightedReference[]>([]);

  // Check if Annie is enabled
  const annieEnabled = getSetting<boolean>('annie.enabled');

  // Register the handler for the Anni chat selector plugin
  useEffect(() => {
    setAnniChatHandler({
      insertReference: (noteId: string, noteName: string, selectedText: string, textStart: number, textEnd: number) => {
        // Format the reference using new syntax: [Note Name][noteId:text:thetexthere]
        // Include character positions: [Note Name][noteId:text:thetexthere:start:end]
        const referenceMarkdown = `[${noteName}][${noteId}:text:${selectedText}:${textStart}:${textEnd}] `;

        // Get current value and append the reference
        const currentValue = chatInputRef.current?.getValue() || '';
        const newValue = currentValue ? `${currentValue} ${referenceMarkdown}` : referenceMarkdown;

        // Set the input value with the reference
        chatInputRef.current?.setValue(newValue);

        // Add highlight for this reference
        const newHighlight: HighlightedReference = { noteId, textStart, textEnd };
        highlightsRef.current.push(newHighlight);
        setHighlightedReferences([...highlightsRef.current]);

        // Focus the input
        chatInputRef.current?.focus();
      },
    });
  }, [setHighlightedReferences]);

  // Get the latest assistant message
  const latestAssistantMessage = React.useMemo(() => {
    const assistantMessages = messages.filter((msg) => msg.role === 'assistant');
    return assistantMessages[assistantMessages.length - 1];
  }, [messages]);

  // Get the message content to display - prioritize streaming, then latest assistant message
  // When loading a new request, don't show the previous message - show status instead
  const displayedMessage = isLoading
    ? currentStreamingMessage || '' // Only show streaming content when loading, not previous response
    : currentStreamingMessage || latestAssistantMessage?.content || '';

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!chatInputRef.current || isLoading) return;

    const value = chatInputRef.current.getValue();
    if (!value.trim() || isLoading) return;

    submitMessage(value);
    // Clear the input and highlights after submission
    chatInputRef.current.setValue('');
    highlightsRef.current = [];
    setHighlightedReferences([]);
  };

  const handleChatSubmit = useCallback(
    (value: string): void => {
      if (!value.trim() || isLoading) return;
      submitMessage(value);
      // Clear the input and highlights after submission
      if (chatInputRef.current) {
        chatInputRef.current.setValue('');
      }
      highlightsRef.current = [];
      setHighlightedReferences([]);
    },
    [isLoading, submitMessage, setHighlightedReferences],
  );

  const handleMicClick = async (): Promise<void> => {
    if (isRecording) {
      setIsTranscribing(true);
      const audioBlob = await stopRecording();

      if (audioBlob) {
        const result = await transcribeAudio(audioBlob, customFetch);

        if (result.error) {
          console.error('Transcription error:', result.error);
        } else if (result.text && chatInputRef.current) {
          chatInputRef.current.setValue(result.text);
        }
      }

      setIsTranscribing(false);
    } else {
      try {
        await startRecording();
      } catch (recordingError) {
        console.error('Failed to start recording:', recordingError);
      }
    }
  };

  const handleDismissGroup = useCallback(
    (e: React.MouseEvent, groupId: string): void => {
      console.log('[handleDismissGroup] Dismissing group:', groupId);
      e.stopPropagation();
      dismissSuggestionGroupFromBar(groupId);
    },
    [dismissSuggestionGroupFromBar],
  );

  // Don't render Annie if disabled
  if (!annieEnabled) {
    return null;
  }

  const hasAutoSuggestions = availableSuggestionGroups.length > 0;
  const categorizedSuggestions = hasAutoSuggestions ? groupSuggestionsByCategory(availableSuggestionGroups) : {};

  // Show messages if there's a current message/streaming OR if there are no suggestions
  // Messages take priority over auto-suggestions when there's an active conversation
  // Always show messages when there's an error (including rate limits)
  const hasActiveMessage = displayedMessage || isLoading || currentStatusMessage || error;
  // Always show messages when there's an error, even if there are auto-suggestions
  const shouldShowMessages = hasActiveMessage || !hasAutoSuggestions || error !== null;

  anniChatLogger.debug('Rendering with auto suggestions:', {
    hasAutoSuggestions,
    availableSuggestionGroupsCount: availableSuggestionGroups.length,
    availableSuggestionGroups,
    categorizedSuggestions,
    categoryCount: Object.keys(categorizedSuggestions).length,
    hasActiveMessage,
    shouldShowMessages,
  });

  return (
    <Container data-testid="annie-chat-container">
      <Wrapper ref={inputContainerRef} data-testid="annie-wrapper">
        <AcceptRejectAll />

        {!hasAutoSuggestions && <SuggestionGroups />}

        <AnniBar hasAutoSuggestions={hasAutoSuggestions} data-testid="annie-bar">
          <AnniBarHeader>
            <AnniTitle>
              <span style={{ transform: 'translateY(2px)', display: 'inline-block' }}>
                <AIIcon width="16px" height="16px" />
              </span>
              Anni{hasAutoSuggestions && ' Suggestions'}
            </AnniTitle>
            {shouldShowMessages && (
              <span style={{ marginLeft: '8px', display: 'inline-block' }}>
                {error ? (
                  <ErrorMessage data-testid="annie-message" data-error="true">
                    {error.trim() || 'An error occurred'}
                  </ErrorMessage>
                ) : (
                  <>
                    {displayedMessage && <span data-testid="annie-message">{displayedMessage}</span>}
                    {isLoading && !displayedMessage && !currentStatusMessage && (
                      <LoadingDots data-testid="annie-chat-loading" />
                    )}
                    {currentStatusMessage && !displayedMessage && (
                      <StatusMessage data-testid="annie-streaming-status"> {currentStatusMessage}</StatusMessage>
                    )}
                  </>
                )}
              </span>
            )}
          </AnniBarHeader>

          {hasAutoSuggestions && (
            <SuggestionsContainer>
              {Object.entries(categorizedSuggestions).map(([category, groups]) => {
                if (groups.length === 0) return null;

                return groups.map((group) => {
                  const parsedTitle = parseSuggestionTitle(group.title);
                  return (
                    <CategoryRow
                      key={group.id}
                      onClick={() => {
                        console.log('[AnnIChat] Clicked suggestion row, applying group:', group.id);
                        applySuggestionGroup(group.id);
                      }}
                      data-testid={`suggestion-group-${group.id}`}
                      data-group-id={group.id}
                      title={group.title}
                    >
                      <CategoryLabel>{category}</CategoryLabel>
                      <NotesContainer>{getCompactPillDisplay(parsedTitle)}</NotesContainer>
                      <RowDismiss
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDismissGroup(e, group.id);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleDismissGroup(e as unknown as React.MouseEvent, group.id);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        data-testid={`dismiss-group-${group.id}`}
                        title="Dismiss this suggestion"
                      >
                        <X size={14} />
                      </RowDismiss>
                    </CategoryRow>
                  );
                });
              })}
            </SuggestionsContainer>
          )}
        </AnniBar>

        <InputForm onSubmit={handleSubmit}>
          <ChatInput
            ref={chatInputRef}
            placeholder={
              isRecording ? 'Recording...' : 'Ask Anni to summarize, write, or explain your notes and articles'
            }
            disabled={isLoading || isRecording || isRateLimited}
            onSubmit={handleChatSubmit}
          />
          {isSupported && (
            <MicButton
              type="button"
              onClick={handleMicClick}
              disabled={isLoading || isTranscribing}
              isRecording={isRecording}
              title={isRecording ? 'Stop recording' : 'Start voice input'}
            >
              {isRecording ? (
                <Square size={ui.getIconSize('button')} fill="currentColor" />
              ) : (
                <Mic size={18} strokeWidth={2} />
              )}
            </MicButton>
          )}
          <SendButton
            type="submit"
            disabled={isLoading || isRecording || isRateLimited}
            data-testid="annie-chat-submit"
          >
            <CircleArrowUp size={18} strokeWidth={2} />
          </SendButton>
        </InputForm>
      </Wrapper>
    </Container>
  );
};
